import { useMemo, useState } from 'react';
import { StediPayerSearch, type StediPayerSearchOption } from '@features/payer-search';
import { useAdminPayers, usePayers } from '@features/payer-search/store/hooks';
import { useDebounce } from '@hooks/useDebounce';
import { Box, FormControl, InputLabel, MenuItem, Select } from '@mui/material';

import { initialPayerPlanTypes } from './payerPlans';
import { payerRosterRowId } from './payerRosterIdentity';
import type { PayerEnrollmentStatus, PayerRow } from './types';

/**
 * Which directory endpoint backs the search. Both scopes read the same STEDI
 * payer network and return the same row shape (`PayerResponse`); they differ
 * only in the endpoint's authorization: `admin` reads `GET /v2/admin/payers`,
 * `provider` reads `GET /v2/provider/payers`.
 */
export type IntakePayerSearchScope = 'admin' | 'provider';

interface IntakePayerSearchProps {
  existingRows: PayerRow[];
  onAdd: (row: PayerRow) => void;
  /**
   * Required, with no default: an unstated scope would quietly read the staff
   * endpoint and 403 for a provider, which is a runtime failure rather than a
   * build one.
   */
  scope: IntakePayerSearchScope;
}

const helperTextByScope: Record<IntakePayerSearchScope, string> = {
  admin: 'Admin only · select a payer to add it to the selected section',
  provider: 'Select a payer to add it to the list you want us to file with',
};

/**
 * Read the payer directory through the endpoint the actor is allowed to use.
 *
 * Both hooks are called unconditionally (hook rules) and the unused one is
 * skipped, so exactly one request is ever in flight.
 */
function usePayerDirectory(scope: IntakePayerSearchScope, search: string | null) {
  const options = { perPage: 50, search };
  const adminResult = useAdminPayers(options, { skip: search === null || scope !== 'admin' });
  const providerResult = usePayers(options, { skip: search === null || scope !== 'provider' });
  return scope === 'admin' ? adminResult : providerResult;
}

export function IntakePayerSearch({ existingRows, onAdd, scope }: IntakePayerSearchProps) {
  const [search, setSearch] = useState('');
  const [enrollmentStatus, setEnrollmentStatus] =
    useState<PayerEnrollmentStatus>('already_enrolled');
  const [selectionError, setSelectionError] = useState<string | null>(null);
  const debouncedSearch = useDebounce(search.trim(), 300);
  const { payers, isLoading, isFetching, isError } = usePayerDirectory(
    scope,
    debouncedSearch.length >= 2 ? debouncedSearch : null,
  );
  const options = useMemo<StediPayerSearchOption[]>(
    () =>
      payers.map(payer => ({
        avatarUrl: payer.avatar_url,
        displayName: payer.display_name,
        payerId: payer.primary_payer_id,
        stediId: payer.stedi_id,
      })),
    [payers],
  );
  const payerByStediId = useMemo(
    () => new Map(payers.map(payer => [payer.stedi_id, payer])),
    [payers],
  );
  const selectPayer = (option: StediPayerSearchOption | null) => {
    const payer = option ? (payerByStediId.get(option.stediId) ?? null) : null;
    if (!payer) return;
    const payerId = payer.primary_payer_id?.trim();
    if (!payerId) {
      setSelectionError('This payer does not have a primary payer ID and cannot be added.');
      return;
    }
    if (
      existingRows.some(row =>
        row.stediId ? row.stediId === payer.stedi_id : row.payerId === payerId,
      )
    ) {
      setSelectionError('That payer is already in the practice roster.');
      return;
    }
    onAdd({
      displayName: payer.display_name,
      enrollmentStatus,
      jotbill: false,
      // jotcred is derived from the section: payers who want to get
      // credentialed always include JotCred; credentialed-elsewhere payers never do.
      jotcred: enrollmentStatus === 'wants_to_enroll',
      payerId,
      rowId: payerRosterRowId({ payer_id: payerId, stedi_id: payer.stedi_id }),
      stediId: payer.stedi_id,
      programs: payer.programs ?? [],
      ptanRequired: payer.ptan_required ?? null,
      planTypes: initialPayerPlanTypes(payer.programs),
    });
    setSearch('');
    setSelectionError(null);
  };

  return (
    <>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', gap: 1 }}>
        <FormControl size="small" sx={{ flex: '0 1 240px', minWidth: 220 }}>
          <InputLabel id="payer-enrollment-section-label">Add payer to</InputLabel>
          <Select
            label="Add payer to"
            labelId="payer-enrollment-section-label"
            onChange={event => setEnrollmentStatus(event.target.value)}
            value={enrollmentStatus}
          >
            <MenuItem value="already_enrolled">Credentialed elsewhere</MenuItem>
            <MenuItem value="wants_to_enroll">Wants to get credentialed</MenuItem>
          </Select>
        </FormControl>
        <Box sx={{ flex: '1 1 280px', minWidth: 0 }}>
          <StediPayerSearch
            error={isError}
            helperText={helperTextByScope[scope]}
            inputValue={search}
            loading={isLoading || isFetching}
            onInputChange={setSearch}
            onSelect={selectPayer}
            options={options}
            placeholder="Search payers to add (e.g., Ambetter, Lucet, Tricare…)"
          />
        </Box>
      </Box>
      {selectionError && (
        <Box role="alert" sx={{ color: 'error.main', fontSize: '0.75rem', mt: 0.5 }}>
          {selectionError}
        </Box>
      )}
    </>
  );
}
